import { useCallback, useEffect, useRef, useState } from 'react'

import { postCreateWelcomeLead } from '@/hooks/useCreateWelcomeLead'
import { usePageLoad } from '@/hooks/usePageLoad'
import {
  getRudderstackAnonymousId,
  isRudderstackLoaded,
  subscribeToRudderstackLoaded,
  trackRudderstackEvent,
} from '@/hooks/useRudderstackAnalytics'
import { isSmsMarketingEnabled } from '@/hooks/useSmsMarketingEnabled'
import { brand } from '@/utils/brand'
import { openSmsOnlyModal, POST_CAPTURE_QUIET_MS } from '@/utils/smsOptInModalQueue'
import { trackGenerateLead } from '@/utils/trackGenerateLead'

import type * as BrazeSDKModule from '@braze/web-sdk'

type BrazeSDK = typeof BrazeSDKModule

// Lazy-load @braze/web-sdk only in the browser. Top-level
// `import * as braze from '@braze/web-sdk'` runs at module-evaluation time —
// even when the consuming Client Component is just being SSR'd — and the
// SDK references `navigator.platform` at the top of its
// browser-detector.js, which crashes Node with
// "ReferenceError: navigator is not defined". The dynamic require below
// runs only after the window guard so the SDK never loads server-side.
let cachedBrazeSDK: BrazeSDK | null = null
const loadBrazeSDK = (): BrazeSDK | null => {
  if (typeof window === 'undefined') return null
  if (!cachedBrazeSDK) {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    cachedBrazeSDK = require('@braze/web-sdk') as BrazeSDK
  }
  return cachedBrazeSDK
}

// Lead-capture POST dedup is centralized inside postCreateWelcomeLead so every
// caller (this iframe listener, the postMessage handler in ClientPortal, the
// React Query mutation in useCreateWelcomeLead) shares one dedupe set. We
// still bail on invalid emails here to avoid firing a noisy Braze event.
/**
 * Dismisses the Braze in-app message currently on screen, so its own
 * confirmation screen never renders and the SMS modal can take that slot.
 * Safe to call when nothing is showing.
 */
export const closeActiveBrazeInAppMessage = (): void => {
  if (typeof window === 'undefined') return

  const g = getGlobal()
  const active = g.__brazeActiveInAppMessage
  g.__brazeActiveInAppMessage = null
  if (!active) return

  try {
    active.closeMessage()
  } catch {
    // A message Braze has already torn down throws here; nothing to do.
  }
}

const fireLeadCapture = (email: string, emailSubscribed: boolean): void => {
  const trimmed = email.trim()
  if (!trimmed || !trimmed.includes('@')) return

  // TEMP timing instrumentation (strip once verified): measure submit -> SMS
  // modal shown. `submittedAt` is captured the instant the Braze email modal
  // is submitted (iframe path).
  const submittedAt = performance.now()
  // eslint-disable-next-line no-console
  console.log('[sms-timing] braze email submitted (iframe path)')

  // When SMS marketing is on, the SMS opt-in replaces Braze's message, so we
  // tear Braze's message down at submit (its confirmation screen would otherwise
  // swap in). When SMS marketing is OFF there is no opt-in to replace it, so we
  // leave Braze's message up and let it show its own confirmation on signup.
  if (isSmsMarketingEnabled()) {
    closeActiveBrazeInAppMessage()
  }

  const brazeInstance = getGlobal().__brazeInstance
  brazeInstance?.logCustomEvent('braze_lead_capture_submitted', {
    email: trimmed,
    email_subscribed: emailSubscribed,
    brand: brand.key,
  })
  // This path runs when the campaign HTML does not post back to the parent, so
  // ClientPortal's postMessage handler never fires. Without this push the lead
  // is captured but GTM sees no generate_lead. Deduped against that handler.
  trackGenerateLead(trimmed, 'Braze Lead Capture Modal')
  // Pass the Rudderstack SDK's anonymous_id so the backend's `generate_lead`
  // server-side event stitches to this visitor's existing client-side session
  // in Rudderstack. Undefined before the SDK loads; postCreateWelcomeLead then
  // waits (bounded) for it before posting.
  const anonymousId = getRudderstackAnonymousId()
  // Fire-and-forget: the lead POST is slow in production, so the SMS opt-in must
  // not wait on it. Still drop the dedupe cookie on success so ClientPortal
  // suppresses the 'braze_show_lead_capture' trigger on next load; a failed POST
  // leaves it unset so Braze re-prompts the email capture. (Key/expiry stay in
  // sync with ClientPortal.tsx#LEAD_CAPTURE_COOKIE.)
  postCreateWelcomeLead({ email: trimmed, emailSubscribed, anonymousId })
    .then((res) => {
      if (!res?.success) return
      if (typeof document !== 'undefined') {
        const oneYear = 60 * 60 * 24 * 365
        document.cookie = `braze_lead_capture_shown=1; max-age=${oneYear}; path=/; samesite=lax`
      }
    })
    .catch(() => undefined)

  // Open the SMS opt-in on a fixed short beat after submit, independent of the
  // lead POST above. openSmsOnlyModal self-guards on the SMS-marketing brand
  // flag and the sms_only_modal_shown dedupe cookie.
  window.setTimeout(() => {
    // eslint-disable-next-line no-console
    console.log(
      `[sms-timing] opening SMS-only modal ${Math.round(
        performance.now() - submittedAt,
      )}ms after submit (iframe path)`,
    )
    openSmsOnlyModal(trimmed)
  }, POST_CAPTURE_QUIET_MS)
}

// Fire the "modal shown" impression event, tolerating the Rudderstack SDK-load
// race. On a slow load the SDK instance may not exist yet when the lead-capture
// modal is wired, so a fire-and-forget `track` (rudderModuleRef.current?.track)
// silently no-ops and the impression is dropped. If the SDK is ready, fire now;
// otherwise fire exactly once, the moment it loads. The caller's
// __leadCaptureWired guard already ensures this runs once per modal instance,
// so the load subscription here is per-modal, not per-page.
const trackEmailModalShownWhenReady = (): void => {
  if (isRudderstackLoaded()) {
    trackRudderstackEvent('braze_email_modal_shown')
    return
  }

  let fired = false
  let unsubscribe: (() => void) | null = null
  unsubscribe = subscribeToRudderstackLoaded(() => {
    if (fired) return
    fired = true
    unsubscribe?.()
    trackRudderstackEvent('braze_email_modal_shown')
  })
}

// The Braze lead-capture in-app message renders into an about:srcdoc iframe.
// After showInAppMessage runs, poll the DOM for the iframe, find the email
// input, and attach submit/click listeners that fire the lead POST.
const attachLeadCaptureListener = (): void => {
  let attempts = 0
  const tryAttach = (): void => {
    attempts += 1
    const iframes = document.querySelectorAll<HTMLIFrameElement>('iframe')
    let wired = false

    for (const iframe of iframes) {
      let doc: Document | null = null
      try {
        doc = iframe.contentDocument
      } catch {
        continue
      }
      if (!doc) continue

      type Wired = Document & { __leadCaptureWired?: boolean }
      const d = doc as Wired
      if (d.__leadCaptureWired) {
        wired = true
        continue
      }

      const emailInput = doc.querySelector<HTMLInputElement>(
        'input[type="email"], input[name*="email" i], input[id*="email" i]',
      )
      if (!emailInput) continue

      d.__leadCaptureWired = true
      wired = true

      // Client-side RudderStack "modal shown" event, fired once per lead-capture
      // modal — at the point we've confirmed the email form is actually in the
      // DOM (i.e. the leadgen email modal is on screen), not merely that some
      // in-app message was delivered. Pairs with the generate_lead submit event
      // so PM/marketing can measure email lead-gen take rate/conversion. The
      // __leadCaptureWired guard keeps it to one fire per modal instance; the
      // helper waits out the SDK-load race so slow loads don't drop it.
      trackEmailModalShownWhenReady()

      const handler = (): void => {
        const checkbox = doc!.querySelector<HTMLInputElement>('input[type="checkbox"]')
        fireLeadCapture(emailInput.value, checkbox?.checked ?? false)
      }

      emailInput.closest('form')?.addEventListener('submit', handler, { capture: true })
      doc
        .querySelectorAll<HTMLElement>('button, [role="button"], input[type="submit"]')
        .forEach((btn) => btn.addEventListener('click', handler, { capture: true }))
    }

    if (!wired && attempts < 30) {
      setTimeout(tryAttach, 100)
    }
  }
  setTimeout(tryAttach, 50)
}

interface UseBrazeOptions {
  autoInitialize?: boolean
  enabled?: boolean
  deferUntilLoad?: boolean
}

interface PlacementEntry {
  container: HTMLElement
  onLoad?: (hasContent: boolean) => void
}

interface UseBrazeReturn {
  isInitialized: boolean
  initializeBraze: () => void
  logCustomEvent: (eventName: string, properties?: Record<string, unknown>) => void
  setUserEmail: (email: string) => void
  setUserCustomAttribute: (key: string, value: unknown) => void
  aliasRudderstackUser: () => void
  setupBanner: (
    placementId: string,
    container: HTMLElement,
    onLoad?: (hasContent: boolean) => void,
  ) => void
  setupBanners: (
    placements: {
      placementId: string
      container: HTMLElement
      onLoad?: (hasContent: boolean) => void
    }[],
  ) => void
}

// Use window globals so state is shared even if Next.js bundles this module multiple times
interface BrazeGlobal {
  __brazeInstance: BrazeSDK | null
  __brazeInitialized: boolean
  __brazeInitializing: boolean
  __brazeInitListeners: Set<() => void>
  // The in-app message currently on screen, kept so the lead-capture handoff
  // can tear it down and put the SMS modal in its place.
  __brazeActiveInAppMessage: { closeMessage: () => void } | null
}

const getGlobal = (): BrazeGlobal => {
  const w = window as unknown as BrazeGlobal
  if (!w.__brazeInitListeners) {
    w.__brazeInstance = null
    w.__brazeInitialized = false
    w.__brazeInitializing = false
    w.__brazeInitListeners = new Set()
    w.__brazeActiveInAppMessage = null
  }
  return w
}

const loadAndInitBraze = (): void => {
  if (typeof window === 'undefined') return
  const g = getGlobal()
  if (g.__brazeInitialized || g.__brazeInitializing) return

  const key = process.env.NEXT_PUBLIC_BRAZE_API_SDK_KEY || ''
  const baseUrl = process.env.NEXT_PUBLIC_BRAZE_API_SDK_URL || ''
  const env = (process.env.NEXT_PUBLIC_API_ENV || '').toLowerCase()
  if (!key) return

  const braze = loadBrazeSDK()
  if (!braze) return

  g.__brazeInitializing = true
  g.__brazeInstance = braze
  braze.initialize(key, {
    baseUrl,
    enableLogging: env !== 'production',
    allowUserSuppliedJavascript: true,
    openInAppMessagesInNewTab: false,
  })
  braze.subscribeToInAppMessage((message) => {
    // Held so fireLeadCapture can dismiss this message and hand straight over
    // to the SMS modal, replacing Braze's own confirmation screen.
    const closable = message as unknown as { closeMessage?: () => void }
    g.__brazeActiveInAppMessage =
      typeof closable.closeMessage === 'function'
        ? (closable as { closeMessage: () => void })
        : null

    braze.showInAppMessage(message)
    attachLeadCaptureListener()
  })
  braze.openSession()
  g.__brazeInitialized = true
  g.__brazeInitializing = false
  g.__brazeInitListeners.forEach((fn) => fn())
}

export const useBraze = (options: UseBrazeOptions = {}): UseBrazeReturn => {
  const { autoInitialize = true, enabled = true, deferUntilLoad = false } = options

  const [isInitialized, setIsInitialized] = useState(
    () => typeof window !== 'undefined' && getGlobal().__brazeInitialized,
  )
  const pageLoaded = usePageLoad({ defer: deferUntilLoad })

  // Track registered placements and their containers/callbacks
  const placementsRef = useRef<Map<string, PlacementEntry>>(new Map())
  const subscriptionSetupRef = useRef(false)

  // Subscribe to shared initialization state
  useEffect(() => {
    const g = getGlobal()
    if (g.__brazeInitialized) {
      setIsInitialized(true)
      return
    }

    const listener = (): void => setIsInitialized(true)
    g.__brazeInitListeners.add(listener)
    return () => {
      g.__brazeInitListeners.delete(listener)
    }
  }, [])

  const initializeBraze = useCallback((): void => {
    loadAndInitBraze()
  }, [])

  const handlePlacement = useCallback((placementId: string): void => {
    const braze = getGlobal().__brazeInstance
    if (!braze) return
    const entry = placementsRef.current.get(placementId)
    if (!entry) return
    const { container, onLoad } = entry

    const banner = braze.getBanner(placementId)
    if (!banner) {
      container.style.display = 'none'
      onLoad?.(false)
      return
    }
    if (banner.isControl) {
      // Must call insertBanner for control variants so Braze records the impression
      braze.insertBanner(banner, container)
      container.style.display = 'none'
      onLoad?.(false)
      return
    }

    // Ensure container is visible if we have a non-control banner
    container.style.removeProperty('display')
    braze.insertBanner(banner, container)
    onLoad?.(true)
  }, [])

  const setupBanner = useCallback(
    (placementId: string, container: HTMLElement, onLoad?: (hasContent: boolean) => void): void => {
      if (typeof window === 'undefined') return
      const braze = getGlobal().__brazeInstance
      if (!braze) return
      placementsRef.current.set(placementId, { container, onLoad })

      // Try to render immediately with any cached data
      handlePlacement(placementId)

      // Ask Braze to refresh this placement
      braze.requestBannersRefresh([placementId])
    },
    [handlePlacement],
  )

  const setupBanners = useCallback(
    (
      placements: {
        placementId: string
        container: HTMLElement
        onLoad?: (hasContent: boolean) => void
      }[],
    ): void => {
      if (typeof window === 'undefined' || placements.length === 0) return

      const ids: string[] = []
      for (const { placementId, container, onLoad } of placements) {
        placementsRef.current.set(placementId, { container, onLoad })
        handlePlacement(placementId)
        ids.push(placementId)
      }

      // Braze caps at 10 placement IDs per refresh call
      const braze = getGlobal().__brazeInstance
      if (!braze) return
      for (let i = 0; i < ids.length; i += 10) {
        braze.requestBannersRefresh(ids.slice(i, i + 10))
      }
    },
    [handlePlacement],
  )

  // Auto-initialize once
  useEffect(() => {
    if (!enabled || !autoInitialize || !pageLoaded) return
    loadAndInitBraze()
  }, [autoInitialize, enabled, pageLoaded])

  // One-time subscription to banner updates
  useEffect(() => {
    const braze = getGlobal().__brazeInstance
    if (!braze || !isInitialized || subscriptionSetupRef.current) return
    subscriptionSetupRef.current = true
    braze.subscribeToBannersUpdates(() => {
      placementsRef.current.forEach((_entry, pid) => handlePlacement(pid))
    })
  }, [isInitialized, handlePlacement])

  const logCustomEvent = useCallback(
    (eventName: string, properties?: Record<string, unknown>): void => {
      const braze = getGlobal().__brazeInstance
      if (!braze) return
      braze.logCustomEvent(eventName, { ...(properties ?? {}), brand: brand.key })
    },
    [],
  )

  const setUserEmail = useCallback((email: string): void => {
    const braze = getGlobal().__brazeInstance
    if (!braze || !email) return
    braze.getUser()?.setEmail(email)
    braze.requestImmediateDataFlush()
  }, [])

  const setUserCustomAttribute = useCallback((key: string, value: unknown): void => {
    const braze = getGlobal().__brazeInstance
    if (!braze || !key) return
    // Web SDK accepts string | number | boolean | Date | string[] | null
    const user = braze.getUser()
    user?.setCustomUserAttribute(
      key,
      value as Parameters<NonNullable<typeof user>['setCustomUserAttribute']>[1],
    )
    braze.requestImmediateDataFlush()
  }, [])

  // Reconcile the device-mode and cloud-mode Braze profiles for an anonymous
  // visitor. The Web SDK identifies this device by its own `braze_id`, so
  // events logged through the SDK (e.g. braze_show_lead_capture) land on that
  // profile. RudderStack always sends to Braze in *cloud* mode, and for events
  // with no external_id — like the backend's server-side `generate_lead` — it
  // keys the Braze user on `user_alias { alias_label: 'rudder_id', alias_name:
  // <rudder anonymousId> }`, which would otherwise spawn a *separate*
  // alias-only profile.
  //
  // By claiming that exact alias on this device profile up front (at page load,
  // before the lead modal can be submitted), Braze's find-or-create-by-alias
  // behaviour resolves the later cloud-mode `generate_lead` onto THIS profile.
  // Net result: the show→convert funnel lives on one Braze user. addAlias is
  // idempotent for an alias already owned by the same profile, so repeat calls
  // are harmless.
  const aliasRudderstackUser = useCallback((): void => {
    const braze = getGlobal().__brazeInstance
    if (!braze) return
    const anonymousId = getRudderstackAnonymousId()
    if (!anonymousId) return
    braze.getUser()?.addAlias(anonymousId, 'rudder_id')
    braze.requestImmediateDataFlush()
  }, [])

  return {
    isInitialized,
    initializeBraze,
    logCustomEvent,
    setUserEmail,
    setUserCustomAttribute,
    aliasRudderstackUser,
    setupBanner,
    setupBanners,
  }
}
